/**
 * graft/A5: dev-only builder/user switcher for AI search.
 *
 * Renders a compact bar in the search dropdown ONLY when the search
 * service's dev surface answers the probe (AUTH_DISABLED posture — see
 * devContext.ts for the gating contract). Picking a builder/user stores
 * the override; useAiSearch appends it as query params, which the
 * search service honors only in dev. Clearing falls back to the
 * logged-in identity (JWT claim).
 */
import { useEffect, useState } from "react";

import {
    type DevContext,
    fetchDevUsers,
    loadDevContext,
    probeDevSurface,
    saveDevContext,
} from "./devContext";

interface TenantRow {
    builderId: string;
    servedBy: string;
    entities: number;
}

interface UserRow {
    id: string;
    name: string;
    role: string;
}

export function DevContextSwitcher(props: {
    searchBaseUrl: string;
    isProduction: boolean;
    onChanged: () => void;
}) {
    const { searchBaseUrl, isProduction, onChanged } = props;
    const [tenants, setTenants] = useState<TenantRow[] | null>(null);
    const [users, setUsers] = useState<UserRow[]>([]);
    const [ctx, setCtx] = useState<DevContext>(() => loadDevContext());

    // Probe once; deployed envs never render this (404 => stays null).
    useEffect(() => {
        if (isProduction) return; // belt: never even probe in prod builds
        let cancelled = false;
        void (async () => {
            const surface = await probeDevSurface(searchBaseUrl);
            if (!cancelled && surface) {
                setTenants(surface.tenants);
                setUsers(surface.users);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [searchBaseUrl, isProduction]);

    // Refetch users when the builder override changes. Gated on the probe
    // having resolved (tenants !== null); honest deps — searchBaseUrl was
    // silently un-tracked behind the old eslint-disable (review 1529276),
    // and `hasProbed` keeps the dep primitive so a roster refresh doesn't
    // re-run this on every tenants identity change.
    const hasProbed = tenants !== null;
    useEffect(() => {
        if (!hasProbed) return;
        let cancelled = false;
        void (async () => {
            const roster = await fetchDevUsers(searchBaseUrl, ctx.builderId);
            if (!cancelled) setUsers(roster);
        })();
        return () => {
            cancelled = true;
        };
    }, [ctx.builderId, hasProbed, searchBaseUrl]);

    if (isProduction || tenants === null) return null;

    const synced = tenants.filter((t) => t.servedBy === "alloydb");
    const update = (next: DevContext) => {
        setCtx(next);
        saveDevContext(next);
        onChanged();
    };

    return (
        <div
            data-testid="ai-search-dev-context"
            style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                background: "#f2f5f8",
                borderBottom: "1px solid #e3e8ee",
                fontSize: 12,
                color: "#5a6672",
            }}
        >
            <span
                style={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: 10,
                    color: "#8a94a0",
                }}
            >
                dev
            </span>
            <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                builder
                <select
                    data-testid="ai-search-dev-builder"
                    value={ctx.builderId ?? ""}
                    onChange={(e) =>
                        update({
                            ...ctx,
                            builderId: e.target.value || undefined,
                            userId: undefined,
                        })
                    }
                    style={{ fontSize: 12, maxWidth: 220 }}
                >
                    <option value="">(logged-in builder)</option>
                    {synced.map((t) => (
                        <option key={t.builderId} value={t.builderId}>
                            {t.builderId} · {t.entities.toLocaleString()} records
                        </option>
                    ))}
                </select>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                user
                <select
                    data-testid="ai-search-dev-user"
                    value={ctx.userId ?? ""}
                    onChange={(e) => update({ ...ctx, userId: e.target.value || undefined })}
                    style={{ fontSize: 12, maxWidth: 200 }}
                >
                    <option value="">(logged-in user)</option>
                    {users.map((u) => (
                        <option key={u.id} value={u.id}>
                            {u.name} ({u.role})
                        </option>
                    ))}
                </select>
            </label>
            {(ctx.builderId || ctx.userId) && (
                <button
                    type="button"
                    data-testid="ai-search-dev-clear"
                    onClick={() => update({})}
                    style={{
                        marginLeft: "auto",
                        fontSize: 11,
                        border: "1px solid #cdd6de",
                        borderRadius: 4,
                        background: "#fff",
                        padding: "2px 8px",
                        cursor: "pointer",
                    }}
                >
                    clear override
                </button>
            )}
        </div>
    );
}
